<template>
  <div class="cad_dwg_preview">
    <div ref="cadHost" class="cad_canvas"></div>
    <div class="cad_state" v-if="loading">DWG 加载中...</div>
    <div class="cad_state cad_error" v-if="errorMessage">{{ errorMessage }}</div>
  </div>
</template>

<script>
import { AcApDocManager, eventBus } from "@mlightcad/cad-simple-viewer";

export default {
  name: "CadDwgPreview",
  props: {
    url: {
      type: String,
      required: true,
    },
    knowledgeItem: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      docManager: null,
      failedHandler: null,
      progressHandler: null,
      parserErrorDetail: "",
    };
  },
  mounted() {
    this.initializeViewer();
  },
  beforeDestroy() {
    this.destroyViewer();
  },
  watch: {
    url(newUrl, oldUrl) {
      if (newUrl && newUrl !== oldUrl) {
        this.openDwg();
      }
    },
  },
  methods: {
    getSameOriginBasePath() {
      const configuredBase = process.env.BASE_URL || "/";
      let parsedPath = "/";
      try {
        parsedPath = new URL(configuredBase, window.location.origin).pathname || "/";
      } catch (error) {
        parsedPath = "/";
      }
      return parsedPath.endsWith("/") ? parsedPath : `${parsedPath}/`;
    },
    getRootWorkerUrls() {
      const sameOriginBase = `${window.location.origin}/`;
      return {
        dwgParser: `${sameOriginBase}assets/libredwg-parser-worker.js`,
        mtextRender: `${sameOriginBase}assets/mtext-renderer-worker.js`,
      };
    },
    async initializeViewer() {
      if (!this.$refs.cadHost) return;
      this.loading = true;
      this.errorMessage = "";
      this.parserErrorDetail = "";
      try {
        const workerUrls = this.getRootWorkerUrls();
        this.docManager = AcApDocManager.createInstance({
          container: this.$refs.cadHost,
          autoResize: true,
          webworkerFileUrls: workerUrls,
        });

        this.failedHandler = () => {
          this.errorMessage = "DWG 预览失败，请检查文件是否损坏或格式是否受支持";
          this.loading = false;
        };
        eventBus.on("failed-to-open-file", this.failedHandler);
        this.progressHandler = (payload) => {
          console.log("[CadDwgPreview] open-file-progress:", payload);
          if (
            payload &&
            payload.subStage === "PARSE" &&
            payload.subStageStatus === "ERROR"
          ) {
            const detail = this.extractParserError(payload);
            this.parserErrorDetail = detail;
            if (detail) {
              this.errorMessage = `DWG 解析失败: ${detail}`;
            } else {
              this.errorMessage = "DWG 解析失败，当前解析器不支持该文件内容";
            }
          }
        };
        eventBus.on("open-file-progress", this.progressHandler);

        await this.openDwg();
      } catch (error) {
        console.error("initialize cad viewer failed:", error);
        this.errorMessage = "DWG 预览初始化失败";
        this.loading = false;
      }
    },
    async openDwg() {
      if (!this.docManager || !this.url) return;
      this.loading = true;
      this.errorMessage = "";
      this.parserErrorDetail = "";
      try {
        const fileBuffer = await this.fetchDwgArrayBuffer(this.url);
        this.logDwgHeader(fileBuffer);
        const fileName = this.getDwgFileName();
        const opened = await this.docManager.openDocument(fileName, fileBuffer);
        console.log("[CadDwgPreview] openDocument result:", opened, fileName);
        if (!opened) {
          this.errorMessage = "DWG 预览失败，请检查文件链接或文件内容";
        }
      } catch (error) {
        console.error("open dwg failed:", error);
        this.errorMessage = "DWG 预览失败，请检查文件链接或文件内容";
      } finally {
        this.loading = false;
      }
    },
    getDwgFileName() {
      const suffixRaw =
        (this.knowledgeItem && this.knowledgeItem.fileSuffix) ||
        (this.knowledgeItem &&
          this.knowledgeItem.carrier &&
          this.knowledgeItem.carrier.type) ||
        "dwg";
      const suffix = String(suffixRaw || "dwg")
        .toLowerCase()
        .replace(/^\./, "");

      const nameFromKnowledge =
        (this.knowledgeItem && this.knowledgeItem.name) || "";
      const nameFromUrl = this.extractNameFromUrl(this.url);
      const baseName = (nameFromKnowledge || nameFromUrl || "drawing")
        .replace(/\.[^/.]+$/, "")
        .trim();

      return `${baseName || "drawing"}.${suffix || "dwg"}`;
    },
    extractNameFromUrl(url) {
      if (!url || typeof url !== "string") return "";
      try {
        const urlObject = new URL(url, window.location.origin);
        const lastPath = (urlObject.pathname.split("/").pop() || "").trim();
        return decodeURIComponent(lastPath);
      } catch (error) {
        const path = url.split("?")[0].split("#")[0];
        return decodeURIComponent((path.split("/").pop() || "").trim());
      }
    },
    async fetchDwgArrayBuffer(url) {
      const requestUrl = String(url || "");
      const isSameOrigin = this.isSameOriginUrl(requestUrl);

      if (isSameOrigin) {
        const response = await this.$http.get(requestUrl, {
          responseType: "arraybuffer",
        });
        return response.data;
      }

      const response = await fetch(requestUrl, {
        method: "GET",
        credentials: "omit",
      });
      if (!response.ok) {
        throw new Error(`fetch dwg failed with status ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      console.log("[CadDwgPreview] fetched cross-origin DWG:", {
        status: response.status,
        contentType: response.headers.get("content-type"),
        contentLength: response.headers.get("content-length"),
        byteLength: arrayBuffer.byteLength,
      });
      return arrayBuffer;
    },
    logDwgHeader(fileBuffer) {
      if (!fileBuffer) return;
      try {
        const bytes = new Uint8Array(fileBuffer.slice(0, 12));
        const ascii = Array.from(bytes)
          .map((b) => (b >= 32 && b <= 126 ? String.fromCharCode(b) : "."))
          .join("");
        console.log("[CadDwgPreview] file header ascii:", ascii);
      } catch (error) {
        console.warn("[CadDwgPreview] failed to inspect header:", error);
      }
    },
    extractParserError(payload) {
      const data = (payload && payload.data) || {};
      const raw =
        data.error ||
        data.message ||
        data.msg ||
        data.reason ||
        (data.exception && (data.exception.message || data.exception)) ||
        "";
      let text = "";
      if (typeof raw === "string") {
        text = raw;
      } else if (raw && typeof raw === "object") {
        try {
          text = JSON.stringify(raw);
        } catch (error) {
          text = String(raw);
        }
      } else {
        text = "";
      }
      if (!text) {
        try {
          text = JSON.stringify(data);
        } catch (error) {
          text = "";
        }
      }
      console.error("[CadDwgPreview] parser error detail:", {
        payload,
        data,
        normalizedMessage: text,
      });
      return text;
    },
    isSameOriginUrl(url) {
      try {
        const target = new URL(url, window.location.origin);
        return target.origin === window.location.origin;
      } catch (error) {
        return false;
      }
    },
    async destroyViewer() {
      if (this.failedHandler) {
        eventBus.off("failed-to-open-file", this.failedHandler);
        this.failedHandler = null;
      }
      if (this.progressHandler) {
        eventBus.off("open-file-progress", this.progressHandler);
        this.progressHandler = null;
      }
      if (this.docManager && this.docManager.destroy) {
        await this.docManager.destroy();
      }
      this.docManager = null;
    },
  },
};
</script>

<style lang="less" scoped>
.cad_dwg_preview {
  width: 100%;
  min-height: 420px;
  height: 100%;
  position: relative;
}

.cad_canvas {
  width: 100%;
  height: 100%;
  min-height: 420px;
}

.cad_state {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
}

.cad_error {
  background: rgba(255, 78, 78, 0.9);
}
</style>
